import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalMilestone } from '../../core/models/portal-milestone';
import { CheckIcon } from '../../ui/portal-icons';

/**
 * Where the case has got to, drawn as a rail rather than as a list of coloured dots.
 *
 * **The connecting line is what makes it a progress indicator.** Three unattached dots in three
 * shades are a legend a customer has to work out; a line that is filled behind the current step and
 * grey in front of it is read without being read. The fill stops at the current step, because the
 * one thing this must not imply is that a stage nobody has started is under way.
 *
 * **Each step says its state in words as well.** The states differ by colour, weight and mark, and
 * the mark is a tick rather than a hue for the ones that are done — but a customer using a screen
 * reader gets none of that, so the state is also a visually hidden word on every row.
 *
 * The rail shows the milestones of the version this case is **pinned** to, so a customer who
 * started before a pipeline change still sees the stages they were told about. Steps the company
 * did not write customer-visible text for are absent rather than blank — an empty rail is the
 * honest answer when nothing was written, and the internal step names are staff language.
 */
export function MilestoneRail({ milestones }: { milestones: readonly PortalMilestone[] }) {
    const { t, language } = useTranslation();

    return (
        <ol className="relative">
            {milestones.map((step, index) => {
                const last = index === milestones.length - 1;
                const stateKey = step.current
                    ? 'portal.step_current'
                    : step.reached
                      ? 'portal.step_done'
                      : 'portal.step_upcoming';

                return (
                    <li key={step.key} className="relative flex gap-4 pb-6 last:pb-0">
                        {/* The line belongs to the step above it, so the last row has none and the
                            rail does not trail off past the final stage. */}
                        {!last && (
                            <span
                                aria-hidden="true"
                                className={`absolute top-7 bottom-0 start-[13px] w-0.5 ${
                                    step.reached && !step.current
                                        ? 'bg-brand-500'
                                        : 'bg-slate-200 dark:bg-slate-800'
                                }`}
                            />
                        )}

                        <span
                            aria-hidden="true"
                            className={`relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full text-white ${
                                step.current
                                    ? 'bg-brand-600 ring-4 ring-brand-100 dark:ring-brand-950'
                                    : step.reached
                                      ? 'bg-brand-500'
                                      : 'border-2 border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
                            }`}
                        >
                            {step.reached && !step.current && <CheckIcon className="size-4" />}
                            {step.current && <span className="size-2 rounded-full bg-white" />}
                        </span>

                        <span className="pt-0.5">
                            <span className="sr-only">{t(stateKey)}: </span>
                            <span
                                className={
                                    step.current
                                        ? 'font-bold text-slate-900 dark:text-slate-100'
                                        : step.reached
                                          ? 'text-slate-700 dark:text-slate-300'
                                          : 'text-slate-400 dark:text-slate-500'
                                }
                            >
                                {language === 'kk' ? step.textKk : step.textRu}
                            </span>
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}
